import { z } from 'zod';
import { baseEntityFields, idSchema, propertyExpressionStringSchema, timezoneSchema, } from './common.schema';
import { ALERT_SEVERITIES } from '../constants/severities';
import { sensorKeySchema, datapointNameSchema } from './sensor.schema';
/**
 * Alerting configuration: rules (priority-ordered routing), escalation
 * chains (multi-stage, repeat-until-ack), recipient groups, and scheduled
 * downtime windows (PRD §2.5, tech-arch §5.3).
 */
/* ------------------------------------------------------------------ *
 * Recipients
 * ------------------------------------------------------------------ */
export const DELIVERY_METHODS = [
    'email',
    'push',
    /** Delivery via a configured integration (desk, dispatch, webhook…). */
    'integration',
];
export const recipientSchema = z
    .object({
    method: z.enum(DELIVERY_METHODS),
    /** userId for email/push; integrationId for integration delivery. */
    userId: idSchema.optional(),
    /** Direct address for non-user email recipients (NOC mailbox etc.). */
    email: z.string().email().optional(),
    integrationId: idSchema.optional(),
})
    .strict()
    .superRefine((val, ctx) => {
    // Required-for-method AND only-allowed-for-method — stale fields must
    // not linger to influence downstream delivery logic.
    if (val.method === 'integration') {
        if (!val.integrationId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['integrationId'],
                message: 'integrationId is required for integration delivery',
            });
        }
        if (val.userId !== undefined || val.email !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'userId/email are not allowed for integration delivery',
            });
        }
    }
    if (val.method === 'email') {
        if (!val.userId && !val.email) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['email'],
                message: 'email delivery requires a userId or an email address',
            });
        }
        if (val.userId !== undefined && val.email !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'provide userId or email for email delivery, not both',
            });
        }
        if (val.integrationId !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['integrationId'],
                message: 'integrationId is not allowed for email delivery',
            });
        }
    }
    if (val.method === 'push') {
        if (!val.userId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['userId'],
                message: 'push delivery requires a userId',
            });
        }
        if (val.email !== undefined || val.integrationId !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'email/integrationId are not allowed for push delivery',
            });
        }
    }
});
export const recipientGroupInputSchema = z
    .object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    recipients: z.array(recipientSchema).min(1).max(100),
})
    .strict();
/** Updates may clear nullable-at-rest fields with an explicit null. */
export const recipientGroupUpdateSchema = recipientGroupInputSchema
    .partial()
    .extend({
    description: z.string().max(2000).nullable().optional(),
})
    .strict();
export const recipientGroupSchema = z
    .object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).nullable(),
    recipients: z.array(recipientSchema).min(1).max(100),
})
    .extend(baseEntityFields)
    .strict();
/* ------------------------------------------------------------------ *
 * Escalation chains
 * ------------------------------------------------------------------ */
/** When a stage is eligible to fire (#6, PRD §2.6): always, only inside
 *  the chain's business-hours window, or only outside it. */
export const STAGE_ACTIVE_HOURS = ['always', 'business', 'after_hours'];
/** Business-hours window a chain's gated stages evaluate against. */
export const chainBusinessHoursSchema = z
    .object({
    /** 0=Sunday … 6=Saturday — the days the window STARTS. */
    daysOfWeek: z.array(z.number().int().min(0).max(6)).min(1).max(7),
    /** HH:mm 24h local to `timezone`. */
    startTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    endTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    timezone: timezoneSchema,
})
    .strict();
/** A stage delivers to its recipients, then waits `escalationMinutes`
 *  before the next stage fires (unless the alert is acked/resolved). */
export const escalationStageSchema = z
    .object({
    /** Inline recipients and/or recipient-group references. */
    recipients: z.array(recipientSchema).max(50).default([]),
    recipientGroupIds: z.array(idSchema).max(20).default([]),
    escalationMinutes: z.number().int().min(1).max(1440),
    activeHours: z.enum(STAGE_ACTIVE_HOURS).default('always'),
})
    .strict()
    .superRefine((val, ctx) => {
    if (val.recipients.length === 0 && val.recipientGroupIds.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'a stage needs at least one recipient or recipient group',
        });
    }
});
const escalationChainBaseSchema = z
    .object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    enabled: z.boolean().default(true),
    stages: z.array(escalationStageSchema).min(1).max(10),
    /** After the last stage, loop back to stage 0 until acknowledged. */
    repeatUntilAck: z.boolean().default(false),
    /** Safety cap on repeat cycles (ignored when repeatUntilAck=false). */
    maxRepeatCycles: z.number().int().min(1).max(20).default(5),
    /** Required iff any stage gates on business/after hours. */
    businessHours: chainBusinessHoursSchema.optional(),
})
    .strict();
/** Gated stages need a window to gate ON — enforced at create; updates
 *  are partial, so the same rule re-runs on the merged stored doc. */
const requireHoursForGatedStages = (val, ctx) => {
    const gated = (val.stages ?? []).some((s) => s.activeHours !== undefined && s.activeHours !== 'always');
    if (gated && !val.businessHours) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'businessHours is required when any stage uses business/after_hours',
            path: ['businessHours'],
        });
    }
};
export const escalationChainInputSchema = escalationChainBaseSchema.superRefine(requireHoursForGatedStages);
/** Updates may clear nullable-at-rest fields with an explicit null. */
export const escalationChainUpdateSchema = escalationChainBaseSchema
    .partial()
    .extend({
    description: z.string().max(2000).nullable().optional(),
    businessHours: chainBusinessHoursSchema.nullable().optional(),
})
    .strict();
export const escalationChainSchema = z
    .object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).nullable(),
    enabled: z.boolean(),
    stages: z.array(escalationStageSchema).min(1).max(10),
    repeatUntilAck: z.boolean(),
    maxRepeatCycles: z.number().int().min(1).max(20),
    businessHours: chainBusinessHoursSchema.nullable().default(null),
})
    .extend(baseEntityFields)
    .strict()
    .superRefine(requireHoursForGatedStages);
/* ------------------------------------------------------------------ *
 * Alert rules (priority-ordered routing)
 * ------------------------------------------------------------------ */
export const alertRuleFiltersSchema = z
    .object({
    /** Minimum severity this rule matches. */
    minSeverity: z.enum(ALERT_SEVERITIES).default('warning'),
    clientIds: z.array(idSchema).max(100).optional(),
    resourceGroups: z.array(z.string().min(1).max(200)).max(100).optional(),
    resourceIds: z.array(idSchema).max(500).optional(),
    sensorKeys: z.array(sensorKeySchema).max(100).optional(),
    datapoints: z.array(datapointNameSchema).max(100).optional(),
    /** Match only alerts of these kinds (#26) — e.g. route `change-escaped` and
     *  `change-unrecovered` to the person doing the change, not the on-call. */
    detectionModes: z.array(z.enum(['outage', 'change-expected', 'change-escaped', 'change-unrecovered'])).min(1).max(4).optional(),
    /** Property expression over resource properties (same engine as
     *  sensor appliesTo). Syntax-validated at write time. */
    propertyExpression: propertyExpressionStringSchema.optional(),
})
    .strict();
export const alertRuleInputSchema = z
    .object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    enabled: z.boolean().default(true),
    /** Lower number = evaluated first; first matching rule routes. */
    priority: z.number().int().min(1).max(10000),
    filters: alertRuleFiltersSchema,
    escalationChainId: idSchema,
    /** Also deliver a notification when the alert resolves. */
    sendResolved: z.boolean().default(true),
    /** Also deliver when the alert is acknowledged. */
    sendAcknowledged: z.boolean().default(false),
})
    .strict();
/** Updates may clear nullable-at-rest fields with an explicit null. */
export const alertRuleUpdateSchema = alertRuleInputSchema
    .partial()
    .extend({
    description: z.string().max(2000).nullable().optional(),
})
    .strict();
export const alertRuleSchema = z
    .object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).nullable(),
    enabled: z.boolean(),
    priority: z.number().int().min(1).max(10000),
    filters: alertRuleFiltersSchema,
    escalationChainId: idSchema,
    sendResolved: z.boolean(),
    sendAcknowledged: z.boolean(),
})
    .extend(baseEntityFields)
    .strict();
/* ------------------------------------------------------------------ *
 * Scheduled downtime (SDT)
 * ------------------------------------------------------------------ */
export const SDT_SCOPE_TYPES = [
    'tenant',
    'client',
    'group',
    'resource',
    'instance',
    'sensor',
    'collector',
];
export const sdtRecurrenceSchema = z
    .object({
    frequency: z.enum(['daily', 'weekly', 'monthly']),
    /** 0=Sunday … 6=Saturday; required for weekly. */
    daysOfWeek: z.array(z.number().int().min(0).max(6)).max(7).optional(),
    /** 1–31; required for monthly. */
    dayOfMonth: z.number().int().min(1).max(31).optional(),
    /** HH:mm 24h local to `timezone`. */
    startTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    endTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    timezone: timezoneSchema,
})
    .strict()
    .superRefine((val, ctx) => {
    // Frequency-specific fields are required for their frequency AND
    // forbidden elsewhere — the scheduler must never see an ambiguous mix.
    if (val.frequency === 'weekly') {
        if (!val.daysOfWeek || val.daysOfWeek.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['daysOfWeek'],
                message: 'weekly recurrence requires daysOfWeek',
            });
        }
        else if (new Set(val.daysOfWeek).size !== val.daysOfWeek.length) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['daysOfWeek'],
                message: 'daysOfWeek must not contain duplicates',
            });
        }
    }
    else if (val.daysOfWeek !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['daysOfWeek'],
            message: `daysOfWeek is not allowed for ${val.frequency} recurrence`,
        });
    }
    if (val.frequency === 'monthly') {
        if (val.dayOfMonth === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['dayOfMonth'],
                message: 'monthly recurrence requires dayOfMonth',
            });
        }
    }
    else if (val.dayOfMonth !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['dayOfMonth'],
            message: `dayOfMonth is not allowed for ${val.frequency} recurrence`,
        });
    }
    if (val.startTime >= val.endTime) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['endTime'],
            message: 'endTime must be after startTime',
        });
    }
});
/**
 * SDT window-shape invariants, enforced on inputs AND stored docs
 * (null and undefined are equivalent "absent" markers here — inputs use
 * optional, stored docs materialize null).
 */
export function sdtWindowShapeRules(val, ctx) {
    const startsAt = val.startsAt ?? null;
    const endsAt = val.endsAt ?? null;
    const recurrence = val.recurrence ?? null;
    const oneTime = startsAt !== null || endsAt !== null;
    if (oneTime && recurrence !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'provide either a one-time window or a recurrence, not both',
        });
    }
    if (!oneTime && recurrence === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'provide a one-time window (startsAt/endsAt) or a recurrence',
        });
    }
    if (oneTime) {
        if (!startsAt || !endsAt) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'one-time windows require both startsAt and endsAt',
            });
        }
        else if (Date.parse(startsAt) >= Date.parse(endsAt)) {
            // Compare instants, not strings — mixed timezone offsets are legal
            // in ISO-8601 and string ordering would mis-handle them.
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['endsAt'],
                message: 'endsAt must be after startsAt',
            });
        }
    }
    if (val.scopeType !== 'tenant' && !val.targetId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['targetId'],
            message: `targetId is required for scopeType "${val.scopeType}"`,
        });
    }
    // Canonical form: tenant-wide windows carry NO target — two
    // serializations of the same logical window would break dedupe/equality.
    if (val.scopeType === 'tenant' && val.targetId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['targetId'],
            message: 'targetId is not allowed for tenant-scoped windows',
        });
    }
}
export const sdtWindowInputSchema = z
    .object({
    scopeType: z.enum(SDT_SCOPE_TYPES),
    /** Target document id; ignored for scopeType `tenant`. */
    targetId: idSchema.optional(),
    comment: z.string().max(2000).optional(),
    /** One-time window (ISO datetimes), or recurrence — exactly one. */
    startsAt: z.string().datetime({ offset: true }).optional(),
    endsAt: z.string().datetime({ offset: true }).optional(),
    recurrence: sdtRecurrenceSchema.optional(),
})
    .strict()
    .superRefine((val, ctx) => sdtWindowShapeRules(val, ctx));
export const sdtWindowSchema = z
    .object({
    scopeType: z.enum(SDT_SCOPE_TYPES),
    targetId: idSchema.nullable(),
    /** DERIVED at create: the client this window belongs to (resolved
     *  from the target), or null for tenant-wide/sensor/group windows.
     *  Drives standard client-scope gating on list/get/delete. */
    clientId: idSchema.nullable(),
    comment: z.string().max(2000).nullable(),
    startsAt: z.string().datetime({ offset: true }).nullable(),
    endsAt: z.string().datetime({ offset: true }).nullable(),
    recurrence: sdtRecurrenceSchema.nullable(),
    /** Set on the companion SDT windows a change window creates (#26): they are
     *  managed by that window and cannot be deleted on their own. Absent on
     *  ordinary windows (and on every window stored before #26). */
    changeWindowId: idSchema.nullable().default(null),
})
    .extend(baseEntityFields)
    .strict()
    // Stored docs enforce the same window-shape invariants as inputs.
    .superRefine((val, ctx) => sdtWindowShapeRules(val, ctx));
//# sourceMappingURL=alerting.schema.js.map